import { createSlice, isPending, isRejected } from '@reduxjs/toolkit'
import { GetKongLocationAllResponseType } from 'features/Location/@types/location'
import {
  InboundTaskQueryParamsType,
  InboundTask,
  GetInboundTaskDetailResponseType,
  ReasonType,
} from 'features/PurchaseOrder/services/inboundTask'
import {
  SLICE_NAME,
  fetchGetLocations,
  fetchInboundTaskList,
  fetchInboundTaskDetail,
  fetchReasons,
  submitInboundTaskPartialGrn,
} from './thunk'

interface InboundTaskStateType {
  isLoading: boolean
  queryParams: Partial<InboundTaskQueryParamsType>
  selectedLocations: GetKongLocationAllResponseType['location']
  constants: {
    locations: GetKongLocationAllResponseType['location']
  }
  inboundTaskListData: InboundTask[]
  inboundTaskDetail: Nullable<GetInboundTaskDetailResponseType['data']>
  modalPartialGrn: {
    isOpen: boolean
    isSubmittingData: boolean
    reasonList: ReasonType[]
    selectedData: Nullable<InboundTask>
  }
}

const initialState: InboundTaskStateType = {
  isLoading: false,
  queryParams: {
    invoice_number: '',
    purchase_order_number: '',
    search_product: '',
    page_index: 1,
    page_size: 20,
  },
  selectedLocations: [],
  constants: {
    locations: [],
  },
  inboundTaskListData: [],
  inboundTaskDetail: null,
  modalPartialGrn: {
    isOpen: false,
    isSubmittingData: false,
    reasonList: [],
    selectedData: null,
  },
}

const inboundTaskSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset: () => initialState,
    setQueryParams: (state, action) => {
      state.queryParams = {
        ...state.queryParams,
        ...action.payload,
      }
    },
    setSelectedLocations: (state, action) => {
      state.selectedLocations = action.payload
    },
    setModalPartialGrn: (state, action) => {
      state.modalPartialGrn = {
        ...state.modalPartialGrn,
        ...action.payload,
      }
    },
    resetModalPartialGrn: (state) => {
      state.modalPartialGrn = initialState.modalPartialGrn
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInboundTaskList.fulfilled, (state, action) => {
        state.isLoading = false
        state.inboundTaskListData = action.payload.data
      })
      .addCase(fetchGetLocations.fulfilled, (state, action) => {
        state.constants.locations = action.payload
      })
      .addCase(fetchInboundTaskDetail.fulfilled, (state, action) => {
        state.isLoading = false
        state.inboundTaskDetail = action.payload.data
      })
      .addCase(fetchReasons.fulfilled, (state, action) => {
        state.modalPartialGrn.reasonList = action.payload.data
      })
      .addCase(submitInboundTaskPartialGrn.fulfilled, (state) => {
        state.modalPartialGrn = initialState.modalPartialGrn
      })
      .addMatcher(isPending(fetchInboundTaskList, fetchInboundTaskDetail), (state) => {
        state.isLoading = true
      })
      .addMatcher(isRejected(fetchInboundTaskList, fetchInboundTaskDetail), (state) => {
        state.isLoading = false
      })
      .addMatcher(isPending(submitInboundTaskPartialGrn), (state) => {
        state.modalPartialGrn.isSubmittingData = true
      })
      .addMatcher(isRejected(submitInboundTaskPartialGrn), (state) => {
        state.modalPartialGrn.isSubmittingData = false
      })
  },
})

export const {
  reset,
  setQueryParams,
  setSelectedLocations,
  setModalPartialGrn,
  resetModalPartialGrn,
} = inboundTaskSlice.actions

export default inboundTaskSlice.reducer
